/** Abandoned carts (GET abandoned-carts): carts left without an order; contact the shopper. Recovery mails belong to Marketing. */
import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Button, Card, Checkbox, Empty, Result, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { fmtDate, listOf, money, toastErr, totalOf, useSite } from './common';

const { Text } = Typography;

const ShopAbandoned = () => {
    const { t } = useTranslation();
    const { api, site } = useSite();
    const [f, setF] = useState({ hours: 1, with_email: true, page: 1 });
    const [data, setData] = useState(null);
    const [soon, setSoon] = useState(false);
    const load = useCallback(async () => {
        try {
            const d = await api.get('/abandoned-carts', { hours: f.hours, with_email: f.with_email ? 1 : undefined, page: f.page, per_page: 25 }, QUIET);
            setData({ items: listOf(d), total: totalOf(d) });
        } catch (e) { if (e?.status === 404) setSoon(true); else toastErr(e); setData({ items: [], total: 0 }); }
    }, [api, f]);
    useEffect(() => { load(); }, [load]);

    const mailto = (c) => {
        const lines = listOf(c.items).map((i) => `- ${i.title}${i.variant_title && i.variant_title !== 'Default' ? ` (${i.variant_title})` : ''} × ${i.quantity}`).join('\n');
        const body = `${t('Hi')} ${c.name || ''},\n\n${t('You left these items in your cart:')}\n${lines}\n\n${site?.url || ''}/cart\n`;
        return `mailto:${c.email}?subject=${encodeURIComponent(t('Your cart at {{name}}', { name: site?.name || '' }))}&body=${encodeURIComponent(body)}`;
    };

    if (soon) return <Card><Result status="info" title={t('Abandoned carts are coming soon')} /></Card>;
    return (
        <Card size="small" title={t('Abandoned carts')}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Select value={f.hours} onChange={(hours) => setF({ ...f, hours, page: 1 })} style={{ width: 200 }}
                    options={[1, 6, 24, 72].map((h) => ({ value: h, label: t('Idle for {{n}}+ hours', { n: h }) }))} />
                <Checkbox checked={f.with_email} onChange={(e) => setF({ ...f, with_email: e.target.checked, page: 1 })}>{t('With email only')}</Checkbox>
            </Space>
            <Table rowKey="cart_id" size="small" loading={!data} dataSource={data?.items} scroll={{ x: 760 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No abandoned carts')} /> }}
                pagination={{ current: f.page, pageSize: 25, total: data?.total, hideOnSinglePage: true, onChange: (page) => setF({ ...f, page }) }}
                columns={[
                    { title: t('Shopper'), key: 's', render: (_, c) => <div><Text>{c.name || c.email || t('Anonymous')}</Text>{c.name && c.email && <div><Text type="secondary" style={{ fontSize: 12 }}>{c.email}</Text></div>}{c.reached_checkout && <Tag color="orange" style={{ marginTop: 2 }}>{t('Reached checkout')}</Tag>}</div> },
                    {
                        title: t('Items'), key: 'i', render: (_, c) => (
                            <Avatar.Group max={{ count: 4 }} size="small">
                                {listOf(c.items).map((i, k) => <Tooltip key={k} title={`${i.title} × ${i.quantity}`}><Avatar shape="square" src={i.image?.src}>{(i.title || '?')[0]}</Avatar></Tooltip>)}
                            </Avatar.Group>
                        ),
                    },
                    { title: t('Value', { context: 'cart' }), key: 'v', width: 120, align: 'right', render: (_, c) => <>{money(c.total, c.currency)}{c.discount_code && <div><Tag style={{ marginInlineEnd: 0 }}>{c.discount_code}</Tag></div>}</> },
                    { title: t('Last activity'), dataIndex: 'updated_at', width: 160, render: (v) => fmtDate(v) },
                    { title: '', key: 'a', width: 60, render: (_, c) => (c.email ? <Tooltip title={t('Email shopper')}><Button size="small" type="text" icon={<MailOutlined />} href={mailto(c)} /></Tooltip> : null) },
                ]} />
        </Card>
    );
};

export default ShopAbandoned;
